import { Button, Grid, Theme, useMediaQuery, Box, FormControl, FormControlLabel, FormGroup, FormLabel, Card, FormHelperText, Typography, Switch, CardHeader, Divider, Tooltip } from "@mui/material"
import { push } from "connected-react-router"
import { ListContextProvider, ReactAdminComponentPropsWithId, ShowBase, useListController, useShowContext, useEditContext, Identifier, ReduxState } from "ra-core"
import { FC, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import LogViewer, { LogViewerProvider, useLogViewerController } from "@/components/backups/LogViewer"
import InfoIcon from '@mui/icons-material/Info';
import { Content } from "@/components/content/Content"
import DatabaseBackupSchedule from "@/components/databases/DatabaseBackupSchedule"
import DatabaseStatistics from "@/components/databases/DatabaseStatistics"
import DatabaseSummary from "@/components/databases/DatabaseSummary"
import DatabaseTipsRecommend from "@/components/databases/DatabaseTipsRecommend"
import { RecPatch } from "@/dataProvider/tools"
import { DatabaseResponseDto } from "@/gen/api/backup"
import { OdResourcePageProps } from "@/types/resource"
import { Link as RouterLink } from 'react-router-dom'
import { parse, pickQueries } from "@/utils/queryStringWrapper"
import TopIndexSizeChart from "@/components/databases/TopIndexSizeChart"
import DatabaseSizeChart from "@/components/databases/DatabaseSizeChart"
import TopRowCountChart from "@/components/databases/TopRowCountChart"
import { useResourceRegister } from "@/utils/resourceRegister"
import { MetricsContextProvider, useMetricsContext, useMetricsController } from "./useMetricsController"
import { TimeRangeType } from "@/gen/api/metrics"
import { useAllDatabasesSource, useDatabaseSelectCard } from "@/components/databases/DatabaseSelectCard"
import { useEffect } from "react"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"
import { MoreOptionsMenu } from "./DatabaseDetail.moreActions"
import Edit from "@/controller/Edit"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { MANAGE_DATABASE_OPERATIONS } from "@/constants"
import { useSimpleSaveAction } from "@/controller/SaveContextProvider"


const DatabaseDetail: FC<ReactAdminComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'dbmetrics-dbsize' })
  useResourceRegister({ name: 'dbmetrics-indexsize' })
  useResourceRegister({ name: 'dbmetrics-rowcount' })
  useResourceRegister({ name: 'database-notification-alert' })

  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '')
  return (
    <ShowBase id={id} {...rest} mutationMode={'pessimistic'} >
      <DatabaseDetailCater {...{id}} {...props} />
    </ShowBase>
  )
}


const DatabaseDetailCater: FC<OdResourcePageProps> = (props) => {
  const { location } = props
  const { record } = useShowContext<DatabaseResponseDto & RecPatch>()
  const dispatch = useDispatch()
  
  const mode = pickQueries(parse(location?.search || ''))('mode') === 'metrics' ? 'metrics' : 'detail'

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/databases/$0/show${location?.search || ''}`, db.databaseId || 0, db.name)))
  }, [dispatch, location?.search])

  const metricsCtrl = useMetricsController({ preDbId: record?.databaseId })
  return (
    <MetricsContextProvider value={metricsCtrl}>
      <EncryptedDatabasesContextProvider>
        <DatabaseDetailBody 
          databaseInfo={record} 
          onSwitchDatabase={handleSwitchDatabase} 
          mode={mode}
          {...props} 
        />
      </EncryptedDatabasesContextProvider>
    </MetricsContextProvider>
  )
}

export type PageMode = 'detail' | 'metrics'

export interface DatabaseDetailBodyProps extends OdResourcePageProps {
  databaseInfo: DatabaseResponseDto | undefined;
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  mode: PageMode;
}

interface NotificationStatus {
  id: Identifier,
  isNotificationEnabled: boolean,
}

const DatabaseNotificationStatus: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { record: notificationStatus, loading, saving, refetch } = useEditContext<NotificationStatus>()
  const switchNotificationState = useSimpleSaveAction()

  const isEnabled = notificationStatus?.isNotificationEnabled ?? true
  return (
    <Box sx={{ m: 2 }}>
      <FormControl component="fieldset" variant="standard" sx={{ mx: 2 }}>
        <FormLabel component="legend">Enable Notifications</FormLabel>
        <FormGroup sx={{ '& span.MuiTouchRipple-root': { color: 'primary.main' } }}>
          <FormControlLabel 
            label="Enable notifications for this database"
            control={
              <Switch 
                disabled={!hasWritePermit}
                checked={isEnabled} 
                onChange={(e:any) => {
                  if (hasWritePermit && !loading && !saving) {
                    switchNotificationState(e, { onSuccess: refetch })
                  }
                }} 
              />
            }
          />
        </FormGroup>
        <FormHelperText>If disabled, no notification associated with this database will be sent.</FormHelperText>
      </FormControl>
      <Card variant="outlined" sx={{ bgcolor: 'info.light', mt: 4, px: 3, py: 2 }}>
        <Typography variant="subtitle2" sx={{ color: 'info.contrastText' }}>
          Notifications are {isEnabled ? 'enabled' : 'disabled'} for this database.{" "}        
        </Typography>
      </Card>
    </Box>
  )
}

export const DatabaseDetailBody: FC<DatabaseDetailBodyProps> = ({
  location,
  databaseInfo,
  onSwitchDatabase,
  mode = 'detail',
  options,
  permissions = [],
  resource,
}) => {
  const DatabaseSelectCard = useDatabaseSelectCard(useAllDatabasesSource)
  
  const {
    setDbId,
    timeRange,
  } = useMetricsContext()

  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      setDbId(chosenDb.databaseId)
      onSwitchDatabase(chosenDb)
    }
  }, [setDbId, onSwitchDatabase])

  useEffect(() => {
    if (databaseInfo?.databaseId) {
      setDbId(databaseInfo.databaseId)
    }
  }, [databaseInfo?.databaseId, setDbId])

  const logCtrl = useLogViewerController()

  const backupScheduleListCtls = useListController({ resource: 'backup-schedule', basePath: '' })

  const mdHidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const handlers = useDefaultSavedHandlers('Update Database Notification')

  const registered = 
    useSelector<ReduxState, boolean>(store => 
      !!store.admin.resources['database-notification-alert']
    )
    
  if (!registered) { return null }

  return (
    <Content 
      pageName={mode === 'metrics' ? "Database Metrics" : "Database Detail"}
      resourceName={mode === 'metrics' ? "Database Metrics" : "Database Detail"}
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        <>
          {mode === 'metrics' &&
            <Button
              component={RouterLink}
              to={`${location?.pathname}`}
              size="small"
              variant="outlined"
            >
              Back to Detail
            </Button>
          }
          <MoreOptionsMenu
            databaseInfo={databaseInfo}
            permissions={permissions as any}
          />
        </>
      }
    >
      <LogViewerProvider value={logCtrl}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
          >
            <DatabaseSelectCard preSelectDbId={databaseInfo?.databaseId} onChange={handleChange} showDesc={true} />
          </Grid>
          {mode === 'detail' &&
            <>
              <Grid
                item
                xs={12}
              >
                <Card>
                  <CardHeader 
                    title={
                      <>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Enable Notifications
                          <Tooltip title="Enable notification emails and alerts associated with this database.">
                            <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                          </Tooltip>
                        </Box>
                      </>
                    }
                  />
                  <Divider />
                  <Edit 
                    resource={(databaseInfo?.databaseId) ? 'database-notification-alert' : 'noop'}
                    id={databaseInfo?.databaseId || -1}
                    basePath=""
                    mutationMode="pessimistic"
                    {...handlers}
                  >
                    <DatabaseNotificationStatus />
                  </Edit>
                </Card>
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
              >
                {databaseInfo ? <DatabaseSummary databaseInfo={databaseInfo} /> : null}
              </Grid>
              {false && // backup schedule is not implemented yet
                <Grid
                  item
                  md={6}
                  xs={12}
                  >
                  <ListContextProvider value={backupScheduleListCtls}>
                    <DatabaseBackupSchedule />
                  </ListContextProvider>
                </Grid>
              }
              <Grid
                item
                md={6}
                xs={12}
                >
                {databaseInfo ? <DatabaseStatistics databaseInfo={databaseInfo} /> : null}
              </Grid>
              <Grid
              item
              md={6}
              xs={12}
              >
                <DatabaseTipsRecommend />
              </Grid>
            </>
          }
          {mode === 'metrics' &&
            <>  
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <DatabaseSizeChart databaseInfo={databaseInfo} />}
              </Grid>
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <TopRowCountChart databaseInfo={databaseInfo} />}
              </Grid>
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <TopIndexSizeChart databaseInfo={databaseInfo} />}
              </Grid>
            </>
          }
        </Grid>
        <LogViewer />
      </LogViewerProvider>
    </Content>
  )
}


export default DatabaseDetail;




